/**
 * Barcode Configuration for AutoPrint Scanner Compatibility
 *
 * Tested on Zebra thermal printers at 203-300 DPI.
 * CODE128 format required for AutoPrint integration.
 */

/**
 * Standard barcode configuration for all labels.
 * These values are optimized for thermal label printing.
 *
 * CODE128 selected because:
 * - Supports full ASCII character set
 * - High density (smaller footprint)
 * - Widely supported by barcode scanners
 * - AutoPrint keyboard wedge compatible
 */
export const BARCODE_CONFIG = {
  /** Barcode format - CODE128 for ASCII support */
  format: 'CODE128' as const,
  /** Bar width multiplier (affects overall width) */
  width: 1.5,
  /** Minimum 40px for reliable scanning at arm's length */
  height: 40,
  /** SKU text font size below barcode */
  fontSize: 10,
  /** No margin (label handles padding) */
  margin: 0,
  /** Show SKU text below barcode */
  displayValue: true,
  /** White background for thermal printing */
  background: '#ffffff',
  /** Black bars for maximum contrast */
  lineColor: '#000000',
} as const;

/** Type for barcode config (for prop typing) */
export type BarcodeConfig = typeof BARCODE_CONFIG;

/**
 * Compact barcode configuration for smaller labels.
 * Optimized for 57x32mm compact labels.
 */
export const COMPACT_BARCODE_CONFIG = {
  /** Barcode format - CODE128 for ASCII support */
  format: 'CODE128' as const,
  /** Narrower bar width for compact space */
  width: 1.0,
  /** Shorter height for compact labels */
  height: 24,
  /** Smaller font size */
  fontSize: 7,
  /** No margin (label handles padding) */
  margin: 0,
  /** Show SKU text below barcode */
  displayValue: true,
  /** White background for thermal printing */
  background: '#ffffff',
  /** Black bars for maximum contrast */
  lineColor: '#000000',
} as const;

/**
 * Validates SKU can be encoded as CODE128.
 * CODE128 supports ASCII 0-127.
 *
 * @param sku - SKU string to validate
 * @returns Object with valid flag and optional error message
 *
 * @example
 * const result = validateBarcodeContent('TSHIRT-BLK-001');
 * if (!result.valid) {
 *   console.error(result.error);
 * }
 */
export function validateBarcodeContent(sku: string): {
  valid: boolean;
  error?: string;
} {
  if (!sku || sku.length === 0) {
    return { valid: false, error: 'SKU is empty' };
  }
  if (sku.length > 30) {
    return { valid: false, error: 'SKU too long (max 30 characters)' };
  }
  if (!/^[\x20-\x7F]+$/.test(sku)) {
    return { valid: false, error: 'SKU contains non-ASCII characters' };
  }
  return { valid: true };
}

/**
 * Check if SKU is valid for barcode (simple boolean check).
 *
 * @param sku - SKU string to validate
 * @returns true if valid for barcode encoding
 */
export function isValidBarcodeContent(sku: string): boolean {
  return validateBarcodeContent(sku).valid;
}

/**
 * Sanitize SKU for barcode rendering.
 * Removes non-ASCII characters and truncates if needed.
 *
 * @param sku - SKU string to sanitize
 * @returns Sanitized SKU safe for CODE128 encoding
 */
export function sanitizeBarcodeContent(sku: string): string {
  // Remove non-ASCII characters
  const ascii = sku.replace(/[^\x20-\x7F]/g, '');
  // Truncate to 30 chars (practical limit for thermal labels)
  return ascii.slice(0, 30);
}
